<template>
    <div @click="editField" v-if="phoneLayout">
        <div
            class="text-grey-600 flex items-center"
            :class="{ 'text-blue-500': hasSelection }"
            v-if="icon"
        >
            <icon :class="[iconSize]" :name="icon" />
            <span class="ml-1 text-lg" v-if="hasSelection">{{ selectedCount }}</span>
        </div>

        <div
            class="link-grey-dark flex items-center"
            :class="{ disabled: disabled, 'form-control': !externalTrigger }"
            v-else
        >
            <span class="flex-1 truncate text-left">{{ selectionText }}</span>
            <icon class="text-grey-700" name="regular/arrow-down-1" v-if="!externalTrigger" />
        </div>

        <select-picker-modal
            :allow-empty="allowEmpty"
            :auto-apply="autoApply"
            :identifier="effectiveValueKey"
            :label="effectiveLabelKey"
            :custom-label="customLabel"
            :multiple="multiple"
            :options="modalOptions"
            :value="currentModalSelection"
            @cancel="emit('cancel')"
            @input="handleModalInput"
            v-model:opened="modalOpened"
        />
    </div>

    <activix-select
        :automatic-dropdown="automaticDropdown"
        :automatic-focus="automaticFocus"
        :automatic-render="automaticRender"
        :clearable="clearable && allowEmpty"
        :close-on-select="closeOnSelect"
        :color="selectColor"
        :custom-label="customLabel"
        :disabled="disabled"
        :filterable="searchable"
        :highlighted="enableHighlight"
        :identifier-key="effectiveValueKey"
        :label-key="effectiveLabelKey"
        :limit="effectiveLimit"
        :limit-selected-visible="limitSelectedVisible"
        :limit-text="limitText"
        :loading="loading"
        :multiple="multiple"
        :none-label="noneLabel"
        :none-selected="noneSelected"
        :nullable="ALLOW_NULLABLE_OPTION"
        :options="options"
        :popover-class-name="popperClass"
        :popover-style="popoverStyle"
        :placeholder="placeholder || t('multiselect.selectOption')"
        :select-all="showSelectAll"
        :selected="effectiveSelection"
        :taggable="taggable"
        :trigger-padding-class-name="triggerPaddingClassName"
        :value-key="effectiveValueKey"
        :with-count="true"
        :with-none="withNone"
        ref="selectRef"
        @input="handleClearInput"
        @open="emit('focus')"
        @update="handleSelectUpdate"
        v-else
    >
        <template #prefix v-if="slots.prefix">
            <slot name="prefix" />
        </template>
    </activix-select>
</template>

<script lang="ts" setup>
    import { ref, computed, watch, onMounted, useSlots } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import useMediaQueries from '@/hooks/useMediaQueries.ts';
    import ActivixSelect from '@/components/activix-ui/ActivixSelect/ActivixSelect.vue';
    import SelectPickerModal from '@/components/lead/inputs/SelectPickerModal.vue';
    // Explicit import needed: the prop named "icon" shadows the global <icon> component
    // in <script setup> binding resolution, causing Vue to use the prop value as a tag name.
    import Icon from '@/components/elements/ActivixIcon.vue';
    import type {
        SelectOption,
        SelectColor,
        SelectedPropValue,
    } from '@/components/activix-ui/ActivixSelect/types.ts';

    type OptionType = SelectOption | string | number;

    const props = withDefaults(
        defineProps<{
            /** Controls whether current selection can be cleared (does not add an empty option row). */
            allowEmpty?: boolean;
            autoApply?: boolean;
            /** Opens the dropdown automatically when the component is mounted */
            automaticDropdown?: boolean;
            /** Focuses the trigger element automatically when the component is mounted */
            automaticFocus?: boolean;
            /** Renders dropdown options immediately instead of waiting for user interaction */
            automaticRender?: boolean;
            clearable?: boolean;
            closeOnSelect?: boolean;
            customLabel?: (option: any, label: string) => string;
            disabled?: boolean;
            enableHighlight?: boolean;
            externalTrigger?: boolean;
            icon?: string;
            iconSize?: string;
            id?: string | null;
            identifier?: string;
            label?: string;
            limit?: number;
            limitSelectedVisible?: number;
            limitText?: (count: number) => string;
            loading?: boolean;
            maxHeight?: number | string;
            minWidth?: number | string;
            multiple?: boolean;
            multipleLimit?: number;
            name?: string;
            noneLabel?: string;
            noneSelected?: boolean;
            options: OptionType[];
            outline?: boolean;
            popperClass?: string;
            placeholder?: string;
            position?: string;
            resetAfter?: boolean;
            searchable?: boolean;
            selectAll?: boolean;
            selected?: SelectedPropValue;
            showLabels?: boolean;
            size?: string;
            taggable?: boolean;
            tooltip?: string;
            trackBy?: string;
            transparent?: boolean;
            value?: SelectedPropValue;
            /** Adds an explicit "None" row in dropdown (legacy-compatible alternative to nullable). */
            withNone?: boolean;
        }>(),
        {
            allowEmpty: true,
            autoApply: false,
            automaticDropdown: false,
            automaticFocus: false,
            automaticRender: false,
            clearable: false,
            customLabel: undefined,
            disabled: false,
            enableHighlight: false,
            externalTrigger: false,
            icon: '',
            iconSize: 'text-base',
            id: null,
            identifier: '',
            label: '',
            limit: 99999,
            limitSelectedVisible: undefined,
            limitText: undefined,
            loading: false,
            maxHeight: undefined,
            minWidth: undefined,
            multiple: false,
            multipleLimit: undefined,
            name: '',
            noneLabel: '',
            noneSelected: false,
            outline: false,
            popperClass: '',
            placeholder: '',
            position: '',
            resetAfter: false,
            searchable: true,
            selectAll: true,
            selected: undefined,
            showLabels: true,
            size: '',
            taggable: false,
            tooltip: '',
            trackBy: '',
            transparent: false,
            value: undefined,
            withNone: false,
        }
    );

    /**
     * Keep nullable disabled in this wrapper to avoid injecting an unexpected "---" option
     * in migrated screens. `allowEmpty` only controls clearing current selection; use `withNone`
     * to render an explicit "None" row with legacy-compatible emitted values.
     */
    const ALLOW_NULLABLE_OPTION = false;

    const emit = defineEmits<{
        (e: 'update', value: OptionType | OptionType[] | null, id?: string | null): void;
        (e: 'input', value: OptionType | OptionType[] | null, id?: string | null): void;
        (e: 'focus'): void;
        (e: 'cancel'): void;
    }>();

    const { t } = useI18n();
    const slots = useSlots();
    const { isUnder } = useMediaQueries();
    const selectRef = ref<InstanceType<typeof ActivixSelect> | null>(null);
    const modalOpened = ref(false);
    const currentModalSelection = ref<any[]>([]);
    const NONE_VALUE = 'none';

    const phoneLayout = computed(() => isUnder('lg'));

    /**
     * Determines if options are primitives (strings/numbers) or objects.
     */
    const isPrimitiveOptions = computed(() => {
        if (!props.options || props.options.length === 0) return false;
        const first = props.options[0];
        return typeof first === 'string' || typeof first === 'number';
    });

    /**
     * The key to use for option values.
     * For primitives, ActivixSelect normalizes to { value: index }, so use 'value'.
     * For objects, prefer the identifier prop, otherwise infer from option shape.
     * This keeps backward compatibility with { value, label } options.
     */
    const effectiveValueKey = computed(() => {
        if (isPrimitiveOptions.value) {
            return 'value';
        }

        if (props.identifier) {
            return props.identifier;
        }

        if (props.trackBy) {
            return props.trackBy;
        }

        const firstOption = props.options.find(
            (option) => option !== null && typeof option === 'object'
        ) as Record<string, unknown> | undefined;

        const sampleOption =
            firstOption &&
            'options' in firstOption &&
            Array.isArray(firstOption.options) &&
            firstOption.options.length
                ? (firstOption.options[0] as Record<string, unknown>)
                : firstOption;

        if (sampleOption && 'value' in sampleOption) {
            return 'value';
        }

        return 'id';
    });

    /**
     * The key to use for option labels.
     * For primitives, ActivixSelect normalizes to { label: String(option) }, so use 'label'.
     * For objects, use the label prop or default to 'label'.
     */
    const effectiveLabelKey = computed(() => {
        return isPrimitiveOptions.value ? 'label' : props.label || 'label';
    });

    const showSelectAll = computed(
        () => !props.taggable && props.selectAll && props.multiple && props.options.length > 1
    );

    const effectiveLimit = computed(() => props.multipleLimit ?? props.limit);

    const triggerPaddingClassName = computed(() => {
        switch (props.size) {
            case 'mini':
                return 'py-1 px-2.5';
            case 'small':
                return 'py-1 px-2.5';
            default:
                return 'py-2 px-2.5';
        }
    });

    function normalizeCssSize(value: number | string | undefined): string | undefined {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }

        if (typeof value === 'number') {
            return `${value}px`;
        }

        return /^\d+$/.test(value) ? `${value}px` : value;
    }

    const popoverStyle = computed<Record<string, string> | undefined>(() => {
        const style: Record<string, string> = {};

        const normalizedMaxHeight = normalizeCssSize(props.maxHeight);
        const normalizedMinWidth = normalizeCssSize(props.minWidth);

        if (normalizedMaxHeight) {
            style.maxHeight = normalizedMaxHeight;
        }

        if (normalizedMinWidth) {
            style.minWidth = normalizedMinWidth;
        }

        return Object.keys(style).length ? style : undefined;
    });

    const effectiveSelection = computed<SelectedPropValue>(() => {
        if (props.selected !== undefined) {
            return props.selected;
        }

        return props.value ?? null;
    });

    function buildNoneOption(): SelectOption {
        const label = props.noneLabel || t('general.none');
        const noneOption: SelectOption = {
            label,
            value: NONE_VALUE,
        };

        if (effectiveLabelKey.value !== 'label') {
            noneOption[effectiveLabelKey.value] = label;
        }

        if (effectiveValueKey.value !== 'value') {
            noneOption[effectiveValueKey.value] = NONE_VALUE;
        }

        return noneOption;
    }

    const modalOptions = computed<OptionType[]>(() => {
        const options = props.options.map((option, index) => {
            if (typeof option === 'string' || typeof option === 'number') {
                return {
                    value: index,
                    label: String(option),
                };
            }

            return option;
        });

        if (props.withNone) {
            options.unshift(buildNoneOption());
        }

        return options;
    });

    const displaySelection = computed<SelectedPropValue>(() => {
        const selection = effectiveSelection.value;

        if (!props.withNone || !props.noneSelected) {
            return selection;
        }

        if (props.multiple) {
            const normalizedSelection = Array.isArray(selection) ? [...selection] : [];
            const hasNoneSelection = normalizedSelection.some((item) =>
                sameIdentifier(resolveIdentifierFromSelection(item as OptionType), NONE_VALUE)
            );

            if (!hasNoneSelection) {
                normalizedSelection.unshift(buildNoneOption());
            }

            return normalizedSelection;
        }

        return buildNoneOption();
    });

    const selectColor = computed<SelectColor>(() => {
        if (props.outline) return 'outline';
        if (props.transparent) return 'transparent';
        return 'white';
    });

    const hasSelection = computed(() => {
        const selection = displaySelection.value;

        if (props.multiple) {
            return Array.isArray(selection) && selection.length > 0;
        }
        return selection !== null && selection !== undefined;
    });

    const selectedCount = computed(() => {
        const selection = displaySelection.value;

        if (props.multiple && Array.isArray(selection)) {
            return selection.length;
        }

        return hasSelection.value ? 1 : 0;
    });

    const selectedLabels = computed<string[]>(() => {
        if (!hasSelection.value) {
            return [];
        }

        const labelKey = effectiveLabelKey.value;
        const selection = displaySelection.value;

        if (props.multiple && Array.isArray(selection)) {
            return selection.map((item) => {
                if (typeof item === 'string' || typeof item === 'number') {
                    return String(item);
                }
                if (props.customLabel) {
                    return props.customLabel(item, labelKey);
                }
                return String((item as SelectOption)[labelKey as keyof SelectOption] || '');
            });
        }

        if (typeof selection === 'string' || typeof selection === 'number') {
            return [String(selection)];
        }

        if (props.customLabel) {
            return [props.customLabel(selection, labelKey)];
        }
        return [String((selection as SelectOption)[labelKey as keyof SelectOption] || '')];
    });

    const selectionText = computed(() => {
        if (!hasSelection.value) {
            return props.placeholder || t('multiselect.selectOption');
        }

        const labels = selectedLabels.value;
        const text = labels.join(', ');

        if (labels.length > (props.limitSelectedVisible || 1) || text.length > 30) {
            const limitFn =
                props.limitText || ((count: number) => t('selectpicker.xSelected', [count]));
            return limitFn(labels.length);
        }

        return text;
    });

    // Track last emitted value to avoid double emission when both @input and @update fire.
    // Using ref to ensure each component instance has its own state.
    const lastEmittedValue = ref<unknown>(Symbol('uninitialized'));

    /**
     * Handles @input for backward compatibility with direct $emit('input', null) calls.
     * Only processes null values (clearing).
     */
    function handleClearInput(value: string | string[] | number | null) {
        if (value === null && lastEmittedValue.value !== null) {
            lastEmittedValue.value = null;
            emitSelection(null);
        }
    }

    function emitSelection(value: OptionType | OptionType[] | null) {
        emit('update', value, props.id);
        emit('input', value, props.id);
    }

    function isPrimitiveValue(value: unknown): value is string | number {
        return typeof value === 'string' || typeof value === 'number';
    }

    function getOptionIdentifier(option: OptionType, fallbackIndex: number): string | number {
        if (isPrimitiveValue(option)) {
            return fallbackIndex;
        }

        const identifier = option[effectiveValueKey.value as keyof SelectOption];

        if (identifier === undefined || identifier === null) {
            return fallbackIndex;
        }

        if (typeof identifier === 'string' || typeof identifier === 'number') {
            return identifier;
        }

        return fallbackIndex;
    }

    function sameIdentifier(
        left: string | number | null | undefined,
        right: string | number | null | undefined
    ): boolean {
        if (left === right) {
            return true;
        }

        if (left === undefined || left === null || right === undefined || right === null) {
            return false;
        }

        return String(left) === String(right);
    }

    function resolveOptionByIdentifier(identifier: unknown): OptionType | null {
        if (props.withNone && sameIdentifier(identifier as string | number, NONE_VALUE)) {
            return buildNoneOption();
        }

        const options = props.options || [];

        for (let index = 0; index < options.length; index += 1) {
            const option = options[index];
            const optionIdentifier = getOptionIdentifier(option, index);

            if (sameIdentifier(optionIdentifier, identifier as string | number)) {
                return option;
            }
        }

        return null;
    }

    function resolveIdentifierFromSelection(selection: OptionType): string | number | null {
        if (isPrimitiveValue(selection)) {
            const primitiveOptionIndex = props.options.findIndex(
                (option) => isPrimitiveValue(option) && String(option) === String(selection)
            );

            if (primitiveOptionIndex !== -1) {
                return getOptionIdentifier(selection, primitiveOptionIndex);
            }

            if (resolveOptionByIdentifier(selection)) {
                return selection;
            }

            // Preserve primitive selections (commonly ids) while options are still loading.
            return selection;
        }

        const selectionIdentifier = selection[effectiveValueKey.value as keyof SelectOption];

        if (typeof selectionIdentifier === 'string' || typeof selectionIdentifier === 'number') {
            return selectionIdentifier;
        }

        const options = props.options || [];

        for (let optionIndex = 0; optionIndex < options.length; optionIndex += 1) {
            const option = options[optionIndex];

            if (option === selection) {
                return getOptionIdentifier(option, optionIndex);
            }
        }

        return null;
    }

    function isIdentifierResolved(
        identifier: string | number | null
    ): identifier is string | number {
        const identifierType = typeof identifier;
        return identifierType === 'string' || identifierType === 'number';
    }

    /**
     * Simple passthrough handler - ActivixSelect handles all transformation.
     */
    function handleSelectUpdate(value: OptionType | OptionType[] | null) {
        // Skip if already emitted by handleClearInput
        if (value === null && lastEmittedValue.value === null) {
            return;
        }

        lastEmittedValue.value = value;
        emitSelection(value);

        if (props.resetAfter) {
            lastEmittedValue.value = props.multiple ? [] : null;
            emitSelection(props.multiple ? [] : null);
        }
    }

    function handleModalInput(value: unknown) {
        if (props.multiple) {
            let identifiers: unknown[] = [];

            if (Array.isArray(value)) {
                identifiers = value;
            } else if (value !== null) {
                identifiers = [value];
            }

            const selection = identifiers
                .map((identifier) => resolveOptionByIdentifier(identifier))
                .filter((item): item is OptionType => item !== null);

            emitSelection(selection);
        } else {
            emitSelection(resolveOptionByIdentifier(value) || null);
        }
    }

    function updateModalSelection() {
        const selection = displaySelection.value;

        if (props.multiple && Array.isArray(selection)) {
            currentModalSelection.value = selection
                .map((item) => resolveIdentifierFromSelection(item as OptionType))
                .filter(isIdentifierResolved);
        } else if (selection !== null && selection !== undefined) {
            const identifier = resolveIdentifierFromSelection(selection as OptionType);
            currentModalSelection.value = isIdentifierResolved(identifier) ? [identifier] : [];
        } else {
            currentModalSelection.value = [];
        }
    }

    function editField() {
        if (props.disabled) {
            return;
        }
        modalOpened.value = true;
    }

    function focus() {
        selectRef.value?.focus?.();
    }

    function blur() {
        selectRef.value?.blur?.();
    }

    watch(
        () => props.automaticDropdown,
        (value) => {
            if (value && phoneLayout.value) {
                modalOpened.value = true;
            }
        },
        { immediate: true }
    );

    watch(displaySelection, updateModalSelection);

    onMounted(() => {
        updateModalSelection();
    });

    defineExpose({
        focus,
        blur,
    });
</script>
